/**
 * extractProposalData.js
 *
 * Pure data extraction for customer proposal view.
 * Takes a scenario object, returns validated proposal data or throws.
 * No React — testable standalone.
 */

/**
 * Extract solver outputs from a scenario object.
 * Same pattern as ScenarioCompareModalV2 getOutputs().
 */
function getOutputs(scenario) {
  const results = scenario.last_run_results || scenario.reopt_results || {};
  const outputs = results.outputs || {};
  return outputs.Scenario?.Site || outputs;
}

/**
 * Format currency with commas and optional decimals.
 */
export function formatCurrency(value, decimals = 0) {
  if (value == null || isNaN(value)) return '$—';
  return '$' + Number(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Format percentage.
 */
export function formatPercent(value, decimals = 1) {
  if (value == null || isNaN(value)) return '—%';
  return (value * 100).toFixed(decimals) + '%';
}

/**
 * Build ESA payment description from config.
 */
function getEsaPaymentDescription(esaConfig) {
  if (!esaConfig) return null;
  const model = esaConfig.model;
  switch (model) {
    case 'fixed_rate':
      return `$${esaConfig.rate}/kWh`;
    case 'savings_share':
      return `${(esaConfig.sharePercent * 100).toFixed(0)}% of savings`;
    case 'fixed_monthly':
      return `$${Number(Math.round(esaConfig.payment * 12)).toLocaleString()}/yr`;
    case 'capacity_payment':
      return `$${esaConfig.ratePerKw}/kW/mo`;
    default:
      return null;
  }
}

/**
 * Extract shared data from a scenario (outputs, costs, system sizes, deal config).
 * Throws if required optimization data is missing.
 */
function extractSharedData(scenario) {
  if (!scenario) {
    throw new Error('No scenario provided');
  }

  const outputs = getOutputs(scenario);
  const uiState = scenario.ui_state || {};
  const form = uiState.form || {};
  const dealConfig = form.dealAnalysisConfig || {};
  const computedResults = dealConfig.computedResults || {};
  const financialSummary = uiState.financialSummary || {};

  // ── BAU cost ───────────────────────────────────────────────────────
  // Priority: financialSummary (frontend financial model) > solver output.
  // Must match the same source as annualSavings below — both come from
  // FinancialAnalysis.projectCashflows[0] so the identity
  // optimizedUtilityBill = bauAnnualCost - annualSavings holds exactly.
  const bauAnnualCost =
    financialSummary.bauYearOneUtilityCost ??
    outputs.ElectricTariff?.year_one_bill_before_tax ??
    null;

  if (bauAnnualCost == null) {
    throw new Error('BAU annual cost not found — run optimization first');
  }

  // ── Annual savings ────────────────────────────────────────────────
  // Priority: financialSummary (frontend financial model — same source as
  // DealAnalysisTab uses for customer economics) > solver output (fallback).
  // Using the solver's year_one_bill_savings as primary caused the proposal
  // to mix solver-based optimizedUtilityBill with frontend-based year1NetSavings,
  // producing visible math contradictions (BAU - NewCost ≠ savings headline).
  const annualSavings =
    financialSummary.annualSavings ??
    outputs.Financial?.year_one_bill_savings ??
    null;

  if (annualSavings == null) {
    throw new Error('Annual savings not found — run optimization first');
  }

  const optimizedUtilityBill = bauAnnualCost - annualSavings;

  // ── System sizes ───────────────────────────────────────────────────
  const system = {
    pvKw: outputs.PV?.size_kw ?? null,
    storageKw: outputs.ElectricStorage?.size_kw ?? null,
    storageKwh: outputs.ElectricStorage?.size_kwh ?? null,
    generatorKw: outputs.Generator?.size_kw ?? null,
  };

  // ── Site info (display-only, optional) ────────────────────────────
  const site = form.site || {};
  const customTariff = form.customTariffData || form.selectedTariffData || {};
  // Tariff data may also be at the scenario root level (saved by ScenarioEditor)
  const rootTariff = scenario.tariff_data || {};
  const siteInfo = {
    city: site.city || null,
    state: site.state || null,
    utilityName: site.utilityName || customTariff.utility_name || rootTariff.utility_name || null,
    tariffName: customTariff.name || rootTariff.name || customTariff.rate_schedule_name || rootTariff.rate_schedule_name || null,
  };

  // ── Financial assumptions (escalation rates + degradation) ──────
  const commercialAssumptions = form.commercialAssumptions || {};
  const escalationRates = commercialAssumptions.escalationRates || {};
  const assumptions = {
    energyEscalation: escalationRates.energy ?? 3.0,
    demandEscalation: escalationRates.demand ?? 3.0,
    laborEscalation: escalationRates.labor ?? 2.5,
    gasDeliveryEscalation: escalationRates.naturalGasDelivery ?? 2.5,
    gasCommodityEscalation: escalationRates.naturalGasCommodity ?? 3.0,
    degradationRate: commercialAssumptions.degradationRate ?? 0.5,
    discountRate: commercialAssumptions.discountRate ?? 8.0,
    analysisYears: commercialAssumptions.analysisYears ?? 20,
    hasPV: system.solarKw != null && system.solarKw > 0,
    hasGenerator: system.generatorKw != null && system.generatorKw > 0,
  };

  // ── VPP revenue (display-only, optional) ─────────────────────────
  const vpp = {
    included: dealConfig.vppIncluded ?? false,
    annualRevenue: computedResults.vppAnnualRevenue ?? null,
    lifetimeRevenue: computedResults.lifetimeVppRevenue ?? null,
    durationYears: dealConfig.vppDurationYears ?? null,
  };

  return { bauAnnualCost, annualSavings, optimizedUtilityBill, system, dealConfig, computedResults, siteInfo, assumptions, vpp };
}

/**
 * Extract proposal data from a scenario (single mode — original behavior).
 *
 * @param {Object} scenario - Full scenario object (from snapshot or live)
 * @returns {Object} Validated proposal data
 * @throws {Error} If required data is missing
 */
export default function extractProposalData(scenario) {
  const shared = extractSharedData(scenario);
  const { dealConfig } = shared;

  // ── Determine mode ─────────────────────────────────────────────────
  const viewMode = dealConfig.viewMode;
  const hasEsa = viewMode === 'esa' || (viewMode === 'compare' && dealConfig.esaConfig);
  const hasCapex = viewMode === 'capex';

  // Default to ESA if no explicit mode but ESA config exists
  const mode = hasCapex ? 'capex' : 'esa';

  if (mode === 'esa') {
    return extractEsaProposal(shared);
  }

  return extractCapexProposal(shared);
}

/**
 * Extract proposal data for ALL available deal modes.
 * Returns { esa, capex } — each is either a proposal object or null.
 */
export function extractAllProposalData(scenario) {
  const shared = extractSharedData(scenario);
  const { dealConfig } = shared;

  // Extract both modes based on config existence (ignore viewMode — share
  // creator controls which modes the customer sees via deal_modes filter).
  const hasEsa = !!dealConfig.esaConfig;
  const hasCapex = !!dealConfig.capexConfig;

  return {
    esa: hasEsa ? extractEsaProposal(shared) : null,
    capex: hasCapex ? extractCapexProposal(shared) : null,
  };
}

/**
 * Extract ESA-mode proposal data.
 */
function extractEsaProposal({
  bauAnnualCost,
  annualSavings,
  optimizedUtilityBill,
  system,
  dealConfig,
  computedResults,
  siteInfo,
  assumptions,
  vpp,
}) {
  const esaConfig = dealConfig.esaConfig || {};
  const year1EsaPayment = computedResults.year1EsaPayment ?? null;
  const year1CustomerSavings = computedResults.year1CustomerSavings ?? null;
  const lifetimeCustomerSavings = computedResults.lifetimeCustomerSavings ?? null;

  // Net savings = utility savings - ESA payment
  const year1NetSavings = year1CustomerSavings ?? (
    year1EsaPayment != null ? annualSavings - year1EsaPayment : annualSavings
  );

  const year1TotalNewCost = year1EsaPayment != null
    ? optimizedUtilityBill + year1EsaPayment
    : null;

  const savingsPercent = bauAnnualCost > 0
    ? year1NetSavings / bauAnnualCost
    : 0;

  return {
    mode: 'esa',
    bauAnnualCost,
    optimizedUtilityBill,
    year1EsaPayment,
    year1TotalNewCost,
    year1NetSavings,
    lifetimeSavings: lifetimeCustomerSavings,
    savingsPercent,
    esaTerms: {
      model: esaConfig.model,
      term: esaConfig.term,
      escalation: esaConfig.escalation,
      paymentDescription: getEsaPaymentDescription(esaConfig),
      year1Payment: year1EsaPayment,
    },
    system,
    annualCashflows: computedResults.customerAnnualCashflows ?? null,
    effectiveRate: computedResults.effectiveRate ?? null,
    rateDiscount: computedResults.rateDiscount ?? null,
    npv: computedResults.customerNpv ?? null,
    siteInfo,
    assumptions,
    vpp,
  };
}

/**
 * Extract CapEx-mode proposal data.
 */
function extractCapexProposal({
  bauAnnualCost,
  annualSavings,
  optimizedUtilityBill,
  system,
  dealConfig,
  computedResults,
  siteInfo,
  assumptions,
  vpp,
}) {
  const capexConfig = dealConfig.capexConfig || {};
  const term = capexConfig.term || 20;

  // CapEx-specific fields from computedResults
  const totalCapex = computedResults.totalCapex ?? null;
  const itcAmount = computedResults.itcAmount ?? null;
  const netCapexAfterItc = computedResults.netCapexAfterItc ?? null;
  const paybackYears = computedResults.paybackYearsWithItc ?? computedResults.paybackYears ?? null;

  // Sale price to customer = totalCapex * (1 + markupPercent/100)
  const markup = capexConfig.markupPercent ?? 20;
  const salePrice = totalCapex != null ? totalCapex * (1 + markup / 100) : null;

  // ITC credit for customer (if eligible)
  const customerItc = capexConfig.customerItcEligible && salePrice != null
    ? salePrice * 0.30  // 30% ITC
    : 0;

  const netCost = salePrice != null ? salePrice - customerItc : null;

  const savingsPercent = bauAnnualCost > 0
    ? annualSavings / bauAnnualCost
    : 0;

  const year1OM = computedResults.year1OpEx ?? null;

  // Compute CapEx customer NPV: year 0 = -salePrice + ITC, then (savings - O&M) per year
  const discountRate = (assumptions.discountRate ?? 8) / 100;
  const escalation = (capexConfig.escalationRate ?? 0.03);
  const omEscalation = 0.02;
  let capexNpv = null;
  if (salePrice != null && annualSavings != null) {
    capexNpv = -salePrice + customerItc; // year 0
    for (let yr = 1; yr <= term; yr++) {
      const savings = annualSavings * Math.pow(1 + escalation, yr - 1);
      const om = (year1OM || 0) * Math.pow(1 + omEscalation, yr - 1);
      capexNpv += (savings - om) / Math.pow(1 + discountRate, yr);
    }
  }

  return {
    mode: 'capex',
    bauAnnualCost,
    optimizedUtilityBill,
    year1AnnualSavings: annualSavings,
    savingsPercent,
    capexTerms: {
      salePrice,
      itcCredit: customerItc,
      netCost,
      paybackYears,
      term,
    },
    system: {
      ...system,
      totalCost: salePrice,
    },
    lifetimeSavings: annualSavings * term,
    year1OM,
    npv: capexNpv,
    siteInfo,
    assumptions,
    vpp,
  };
}
